import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, Clock, Coins, FileText, Globe2 } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { COMPANY } from "@/lib/company";
import type { CountryHub } from "@/content/countries/types";
import { SERVICES } from "@/lib/services";
import { getActiveProvince } from "@/provinces/_registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { LongFormContent } from "@/components/LongFormContent";
import type { PageContent } from "@/content/site-pages";
import {
  CORE_PROGRAMMATIC_SERVICES,
  resolvePair,
} from "@/content/country-service/builder";

export const Route = createFileRoute("/countries/$country/")({
  component: CountryDetail,
  loader: async ({
    params,
  }): Promise<{
    country: CountryHub;
    longform?: PageContent;
    others: { slug: string; nameTh: string; flag: string }[];
  }> => {
    const [{ getCountryBySlug }, { COUNTRY_META }] = await Promise.all([
      import("@/content/countries/registry"),
      import("@/content/countries/meta.gen"),
    ]);
    const country = getCountryBySlug(params.country);
    if (!country) throw notFound();
    const { getCountryLongform } = await import("@/content/longform-countries");
    return {
      country,
      longform: getCountryLongform(country.slug),
      others: COUNTRY_META.filter((c) => c.slug !== country.slug).map((c) => ({
        slug: c.slug,
        nameTh: c.nameTh,
        flag: c.flag,
      })),
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">ไม่พบประเทศนี้</h1>
        <p className="mt-3 text-muted-foreground">
          ลองดูประเทศอื่นที่เราดูแลได้
        </p>
        <Link
          to="/countries"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> กลับไปรายการประเทศ
        </Link>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background p-12 text-center">
      <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
  head: ({ params, loaderData }) => {
    const full = loaderData?.country;
    const country = full;
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    if (!country) {
      return pageHeadForProvince({
        title: "ไม่พบประเทศ — Thai Notary",
        description: "ไม่พบประเทศที่ค้นหา",
        path: `/countries/${params.country}`,
      });
    }
    // Keep title ≤ 60 chars: drop the province suffix (already in <title> elsewhere via og:site_name)
    const title = `${country.flag} ${country.metaTitle}`;
    const base = pageHeadForProvince({
      title,
      description: country.metaDesc,
      path: `/countries/${country.slug}`,
      type: "article",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "ประเทศปลายทาง", url: `${site}/countries` },
              { name: country.nameTh, url: `${site}/countries/${country.slug}` },
            ]),
          ),
        },
        ...(full
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  name: `ขั้นตอนเตรียมเอกสารไป${full.nameTh}`,
                  description: full.intro,
                  totalTime: full.timeline,
                  estimatedCost: {
                    "@type": "MonetaryAmount",
                    currency: "THB",
                    value: full.costRange,
                  },
                  step: full.steps.map((s, i) => ({
                    "@type": "HowToStep",
                    position: i + 1,
                    name: s.title,
                    text: s.desc,
                  })),
                }),
              },
            ]
          : []),
        ...(full && full.faqs.length > 0
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(buildFaqSchema(full.faqs)),
              },
            ]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${site}/countries/${country.slug}#service`,
            name: `Document Legalization for ${country.nameEn}`,
            description: country.metaDesc,
            provider: { "@id": `${site}/#organization` },
            areaServed: [
              { "@type": "Country", name: country.nameEn },
              { "@type": "City", name: config.nameEn },
            ],
            serviceType: country.isApostille
              ? "MFA Apostille + Translation"
              : "MFA + Embassy Legalization + Translation",
            url: `${site}/countries/${country.slug}`,
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/countries/${params.country}`)) },
      ],
    };
  },
});

function CountryDetail() {
  const { country, longform, others } = Route.useLoaderData() as {
    country: CountryHub;
    longform?: PageContent;
    others: { slug: string; nameTh: string; flag: string }[];
  };
  const { config } = getActiveProvince();
  const related = country.relatedServiceSlugs
    .map((slug) => SERVICES.find((s) => s.slug === slug))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));
  // Link to ALL other countries (not just 6) — keeps cross-link graph dense
  // so newer destinations (HK, NZ, Canada, Schengen, China) collect inbound
  // links from every other country page. Projected in the loader so the
  // country meta corpus stays out of the client entry bundle.

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        {/* Hero */}
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <Link
              to="/countries"
              className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
            >
              <ArrowLeft className="h-3 w-3" /> ประเทศทั้งหมด
            </Link>
            <div className="flex items-start gap-4">
              <span className="text-6xl leading-none">{country.flag}</span>
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight md:text-4xl">
                  เตรียมเอกสาร & วีซ่าไป{country.nameTh}
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  {country.nameEn} • {country.region}
                </p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  {country.isApostille && (
                    <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 font-semibold text-emerald-600">
                      <CheckCircle2 className="mr-0.5 inline h-3 w-3" /> รับ Apostille
                    </span>
                  )}
                  <span className="rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                    แปล: {country.language}
                  </span>
                  <span className="rounded-full bg-gold/10 px-2.5 py-1 font-semibold text-gold">
                    บริการจาก{config.nameTh}
                  </span>
                </div>
              </div>
            </div>
            <p className="mt-6 text-base leading-relaxed text-foreground/90">
              {country.intro}
            </p>
          </div>
        </section>

        {/* Quick facts */}
        <section className="border-b border-border/50">
          <div className="mx-auto grid max-w-4xl gap-4 px-4 py-8 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                <Clock className="h-3.5 w-3.5" /> ระยะเวลา
              </div>
              <div className="mt-1 text-sm font-bold">{country.timeline}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                <Coins className="h-3.5 w-3.5" /> ค่าใช้จ่าย
              </div>
              <div className="mt-1 text-sm font-bold">{country.costRange}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                <Globe2 className="h-3.5 w-3.5" /> ภาษา
              </div>
              <div className="mt-1 text-sm font-bold">{country.language}</div>
            </div>
          </div>
        </section>

        {/* Why Us */}
        <section className="border-b border-border/50">
          <div className="mx-auto max-w-4xl px-4 py-10">
            <h2 className="text-xl font-bold md:text-2xl">
              ทำไมเลือกเรา สำหรับเคส{country.nameTh}
            </h2>
            <ul className="mt-4 grid gap-3 md:grid-cols-3">
              {country.whyUs.map((w) => (
                <li
                  key={w}
                  className="flex items-start gap-2 rounded-lg border border-border bg-card p-4 text-sm"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" />
                  {w}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Documents */}
        <section className="border-b border-border/50 bg-muted/20">
          <div className="mx-auto max-w-4xl px-4 py-10">
            <h2 className="flex items-center gap-2 text-xl font-bold md:text-2xl">
              <FileText className="h-5 w-5 text-primary" /> เอกสารที่ต้องเตรียม
            </h2>
            <ul className="mt-4 space-y-2">
              {country.documentsRequired.map((d) => (
                <li
                  key={d}
                  className="flex items-start gap-2 rounded-md bg-background p-3 text-sm"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Steps */}
        <section className="border-b border-border/50">
          <div className="mx-auto max-w-4xl px-4 py-10">
            <h2 className="text-xl font-bold md:text-2xl">ขั้นตอนของเรา</h2>
            <ol className="mt-6 space-y-4">
              {country.steps.map((s, i) => (
                <li
                  key={s.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-5"
                >
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground">
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-bold text-foreground">{s.title}</div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {s.desc}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Tips */}
        {country.tips && country.tips.length > 0 && (
          <section className="border-b border-border/50 bg-gold/5">
            <div className="mx-auto max-w-4xl px-4 py-10">
              <h2 className="text-xl font-bold md:text-2xl">
                💡 Tips จากทีมทนาย
              </h2>
              <ul className="mt-4 space-y-2">
                {country.tips.map((t) => (
                  <li key={t} className="text-sm text-foreground/90">
                    • {t}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* FAQs */}
        {country.faqs.length > 0 && (
          <section className="border-b border-border/50">
            <div className="mx-auto max-w-4xl px-4 py-10">
              <h2 className="text-xl font-bold md:text-2xl">คำถามที่พบบ่อย</h2>
              <div className="mt-6 space-y-4">
                {country.faqs.map((f) => (
                  <div
                    key={f.q}
                    className="rounded-xl border border-border bg-card p-5"
                  >
                    <div className="font-bold text-foreground">{f.q}</div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      {f.a}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Related Services */}
        {related.length > 0 && (
          <section className="border-b border-border/50">
            <div className="mx-auto max-w-4xl px-4 py-10">
              <h2 className="text-xl font-bold md:text-2xl">บริการที่เกี่ยวข้อง</h2>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {related.map((s) => (
                  <Link
                    key={s.slug}
                    to="/services/$service"
                    params={{ service: s.slug }}
                    className="group rounded-lg border border-border bg-card p-4 transition hover:border-primary"
                  >
                    <div className="font-semibold text-foreground group-hover:text-primary">
                      {s.title}
                    </div>
                    <div className="mt-1 line-clamp-1 text-xs text-muted-foreground">
                      {s.desc}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Programmatic Country × Service pair pages — deep dives focused on this destination */}
        <section className="border-b border-border/50 bg-muted/20">
          <div className="mx-auto max-w-4xl px-4 py-10">
            <h2 className="text-xl font-bold md:text-2xl">
              คู่มือเฉพาะปลายทาง {country.flag} {country.nameTh}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              เจาะลึกแต่ละบริการสำหรับการใช้งานใน {country.nameTh} โดยเฉพาะ —
              ขั้นตอน, ค่าใช้จ่าย, และเอกสารปรับตามระบบของประเทศปลายทาง
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {Array.from(
                new Set<string>([
                  ...CORE_PROGRAMMATIC_SERVICES,
                  ...(country.marriageServiceSlug
                    ? [country.marriageServiceSlug]
                    : []),
                  ...(country.visaServiceSlug ? [country.visaServiceSlug] : []),
                ]),
              )
                .map((svcSlug) => resolvePair(country.slug, svcSlug))
                .filter((p): p is NonNullable<ReturnType<typeof resolvePair>> => !!p)
                .map((p) => (
                  <Link
                    key={p.service.slug}
                    to="/countries/$country/$service"
                    params={{
                      country: country.slug,
                      service: p.service.slug,
                    }}
                    className="group rounded-lg border border-border bg-card p-4 transition hover:border-gold"
                  >
                    <div className="text-sm font-bold text-foreground group-hover:text-primary">
                      {p.service.metaTitle.replace(/\s*\|\s*Thai Notary.*/i, "")}
                      <span className="ml-1 text-gold">→ {country.flag}</span>
                    </div>
                    <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                      {p.service.heroSubtitle}
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary/5 py-12">
          <div className="mx-auto max-w-3xl px-4 text-center">
            <h2 className="text-2xl font-extrabold">
              พร้อมเริ่มเคส{country.nameTh}แล้ว?
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              ทีมทนายภูเก็ตของเราดูแลทุกขั้นตอน — โทรปรึกษาฟรี
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <a
                href={`tel:${COMPANY.phoneIntl}`}
                className="rounded-md bg-gold px-6 py-3 text-sm font-bold text-gold-foreground shadow-sm hover:opacity-90"
              >
                📞 {COMPANY.phone}
              </a>
              <a
                href={`https://line.me/R/ti/p/${COMPANY.lineId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-primary px-6 py-3 text-sm font-bold text-primary hover:bg-primary hover:text-primary-foreground"
              >
                LINE {COMPANY.lineId}
              </a>
            </div>
          </div>
        </section>

        {/* Other countries */}
        <section className="border-t border-border/50">
          <div className="mx-auto max-w-4xl px-4 py-10">
            <h2 className="text-base font-bold text-muted-foreground">
              ประเทศอื่นที่เราดูแล
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {others.map((c) => (
                <Link
                  key={c.slug}
                  to="/countries/$country"
                  params={{ country: c.slug }}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm hover:border-primary hover:text-primary"
                >
                  <span>{c.flag}</span>
                  {c.nameTh}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {longform && <LongFormContent content={longform} />}
        <div className="mx-auto max-w-4xl px-4 pb-12">
          <FaqSourceNote topic="consular" />
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}